import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import PayStubModal from './PayStubModal';
import withRoleAccess from '../../hoc/withRoleAccess';
import axios from 'axios';
import { PDFDocument } from 'pdf-lib'; 
import './Teacher.css';
import { useTranslation } from 'react-i18next';
import { Table, Button, Typography, Layout, Card, Space } from 'antd';

const { Title } = Typography;
const { Content } = Layout;

const TeacherPaymentReport = () => {
    const { t } = useTranslation();
    const [paymentReports, setPaymentReports] = useState([]);
    const [selectedPayStub, setSelectedPayStub] = useState(null);
    const API_URL = process.env.REACT_APP_API_URL;

    useEffect(() => {
        const fetchPaymentReports = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(`${API_URL}/teacher-hours/teacher-payment-report`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setPaymentReports(response.data);
            } catch (error) {
                console.error(t('errorFetchingPaymentReports'), error);
            }
        };

        fetchPaymentReports();
    }, [API_URL, t]);

    const formatTotalAmount = (amount) => {
        return typeof amount === 'string' && !isNaN(parseFloat(amount))
            ? parseFloat(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : '0.00';
    };

    const handlePayStubClick = async (report) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/teacher-hours/teacher-payment-report/${report.batch_id}/details`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            const classDetails = response.data.map(cls => ({
                classId: cls.class_id,
                rate: cls.hourly_rate,
                hours: cls.hours_worked,
                total: cls.total_amount
            }));

            const payStubData = {
                companyLogo: "/images/Theplanner.png",
                companyName: "THE PLANNER EDUCATION",
                companyAddress: "The Mercury Ville @Chidlom 3rd Floor, Lumpini, Pathumwan, Bangkok",
                companyWebsite: "www.theplannereducation.com",
                companyPhone: "Tel. 02-253-2533 or 095-726-2666",
                companyLine: "Line: @theplanner",
                teacherName: report.teacher_name,
                teacherAddress: report.address,
                teacherPhone: report.phone,
                teacherNationalId: report.nationalid,
                payPeriod: report.period,
                payDate: report.approval_date,
                classes: classDetails,
                totalHours: report.total_hours,
                totalPay: report.total_amount,
                withholdingTax: report.sum_withholdingtax,
            };

            setSelectedPayStub(payStubData);
        } catch (error) {
            console.error(t('errorFetchingClassDetails'), error);
        }
    };

    const generateTawi50PDF = async (report) => {
        try {
            const existingPdfBytes = await fetch('/images/tawi50.pdf').then(res => res.arrayBuffer());
            const pdfDoc = await PDFDocument.load(existingPdfBytes);
            const form = pdfDoc.getForm();

            form.getTextField('name1').setText('PLANN TECH CO., LTD');
            form.getTextField('add1').setText('770/122 Patio Pattanakarn 30 Suan Luang Bangkok 10250');
            form.getTextField('id1').setText('0103363883993');

            form.getTextField('name2').setText(report.teacher_name || '');
            form.getTextField('add2').setText(report.address || '');
            form.getTextField('id1_2').setText(report.nationalid || '');

            if (Array.isArray(report.classes)) {
                report.classes.forEach((cls, index) => {
                    form.getTextField('date1').setText(report.approval_date || '');
                    form.getTextField(`total${index + 1}`).setText(formatTotalAmount(report.sub_amount || 0));
                    form.getTextField(`spec${index + 1}`).setText(formatTotalAmount(report.sum_withholdingtax || 0));
                });
            }

            form.getTextField('date1').setText(report.approval_date || '');
            form.getTextField('pay1.0').setText(formatTotalAmount(report.sub_amount));
            form.getTextField('tax1.0').setText(formatTotalAmount(report.sum_withholdingtax));
            form.getTextField('total').setText("");

            form.getCheckBox('chk1').check();
            form.getCheckBox('chk8').check();

            const pdfBytes = await pdfDoc.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `Tawi50_${report.teacher_name}_${report.period}.pdf`;
            link.click();
        } catch (error) {
            console.error('Error generating Tawi50 PDF:', error);
        }
    };

    const columns = [
        { title: t('approvalDate'), dataIndex: 'approval_date', key: 'approval_date' },
        { title: t('period'), dataIndex: 'period', key: 'period' },
        { title: t('teacherName'), dataIndex: 'teacher_name', key: 'teacher_name' },
        { title: t('nationalID'), dataIndex: 'nationalid', key: 'nationalid' },
        { title: t('totalHours'), dataIndex: 'total_hours', key: 'total_hours' },
        {
            title: t('subAmount'),
            dataIndex: 'sub_amount',
            key: 'sub_amount',
            render: (amount) => formatTotalAmount(amount)
        },
        {
            title: t('sumWithholdingTax'),
            dataIndex: 'sum_withholdingtax',
            key: 'sum_withholdingtax',
            render: (amount) => formatTotalAmount(amount)
        },
        {
            title: t('totalPay'),
            dataIndex: 'total_amount',
            key: 'total_amount',
            render: (amount) => formatTotalAmount(amount)
        },
        { title: t('approveby'), dataIndex: 'approval_userid', key: 'approval_userid' },
        {
            title: t('paystub'),
            key: 'paystub',
            render: (_, record) => (
                <Button onClick={() => handlePayStubClick(record)}>{t('viewpaystub')}</Button>
            )
        },
        {
            title: t('tawi50'),
            key: 'tawi50',
            render: (_, record) => (
                <Button onClick={() => generateTawi50PDF(record)}>{t('Generate Tawi50')}</Button>
            )
        },
    ];

    return (
        <Card>
            <Sidebar />
                <Header />
                <Content>
                        <Table
                            columns={columns}
                            dataSource={paymentReports}
                            rowKey={(record, index) => index}
                            scroll={{ x: true }}
                        />
                    <PayStubModal 
                        show={selectedPayStub !== null} 
                        onClose={() => setSelectedPayStub(null)} 
                        payStubData={selectedPayStub} 
                    />
                </Content>
        </Card>
    );
};

export default withRoleAccess(TeacherPaymentReport, ['superadmin']);
